import cx from "classnames";
import { Icon } from "common/Icon";
import { CircleSpinner } from "common/spinners";
import { Card } from "common/styleguide/Card";
import { CTA } from "common/styleguide/CTA";
import { Topo } from "common/styleguide/Topo";
import { Distance, Elevation } from "common/units";
import { routeSelectors } from "data/resources/routes";
import { isEmpty } from "features/challenges/challengeUtils";
import { OrgContext } from "features/organizations/OrgContext";
import { commonNouns } from "lib/localization";
import { missingPhoto } from "lib/photoUtils";
import { nlToBr } from "lib/textUtils";
import { getPreferences } from "lib/utils";
import React, { useContext } from "react";
import { defineMessages } from "react-intl";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { messages as overviewMessages } from "../admin/Overview";
import styles from "./DetailsCard.scss";

export const messages = defineMessages({
  editDetails: {
    id: "DetailsCard.editDetails",
    defaultMessage: "Edit Details",
  },
  emptyDesc: {
    id: "DetailsCard.emptyDesc",
    defaultMessage: "Add a description and photos for your Challenge.",
  },
  individualParticipation: {
    id: "DetailsCard.individualParticipation",
    defaultMessage: "Individual participation",
  },
  individualParticipants: {
    id: "DetailsCard.individualParticipants",
    defaultMessage:
      "{count, plural, =0 {individual participants} one {individual participant} other {individual participants}}",
  },
  communityParticipation: {
    id: "DetailsCard.communityParticipation",
    defaultMessage: "Community participation",
  },
  communityParticipants: {
    id: "DetailsCard.communityParticipants",
    defaultMessage:
      "{count, plural, =0 {community participants} one {community participant} other {community participants}}",
  },
  targetDistance: {
    id: "DetailsCard.targetDistance",
    defaultMessage: "Target distance",
  },
  targetElevation: {
    id: "DetailsCard.targetElevation",
    defaultMessage: "Target elevation",
  },
  targetTime: {
    id: "DetailsCard.targetTime",
    defaultMessage: "Target time",
  },
  mostDistance: {
    id: "DetailsCard.mostDistance",
    defaultMessage: "Most distance",
  },
  mostElevation: {
    id: "DetailsCard.mostElevation",
    defaultMessage: "Most elevation",
  },
  mostTime: {
    id: "DetailsCard.mostTime",
    defaultMessage: "Most time",
  },
});

// moment-less preference-aware date format
const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  // `undefined` instead of `'default'` to appease ie11
  const year = d.toLocaleString(undefined, {
    year: "numeric",
    timeZone: "UTC",
  });
  const month = d.toLocaleString(undefined, {
    month: "short",
    timeZone: "UTC",
  });
  const day = d.toLocaleString(undefined, { day: "numeric", timeZone: "UTC" });
  const fmt = getPreferences().date_format;

  switch (fmt) {
    case "dmy":
      return `${day} ${month}, ${year}`;
    case "ymd":
      return `${year} ${month} ${day}`;
    default:
      return `${month} ${day}, ${year}`;
  }
};

export const DetailsCard = ({ goal, metaForGoal, participants, ranks, preview, fm }) => {
  const { isCurrentClub } = useContext(OrgContext);
  const updatedAt = new Date(goal.updatedAt).getTime();
  return (
    <Card noPadding className={styles.detailsCard}>
      <div className={styles.top}>
        {!missingPhoto(goal.cover) ? (
          <div style={{ backgroundImage: `url(${goal.cover}?${updatedAt})` }} className={styles.cover} />
        ) : (
          <Topo dark style={{ height: "100%" }} />
        )}
        <img height="100" width="100" src={`${goal.icon}?${updatedAt}`} className={styles.icon} />
      </div>
      <div className={styles.bottom}>
        <h2>{goal.name}</h2>
        {(!isCurrentClub || preview) && <Settings goal={goal} metaForGoal={metaForGoal} preview={preview} fm={fm} />}
        {goal.description && (
          <div className={styles.description}>{nlToBr(goal.description, { autolink: true, allowDoubleNl: true })}</div>
        )}
        {isEmpty(goal) && <div className={cx(styles.description, styles.empty)}>{fm(messages.emptyDesc)}</div>}
        {isCurrentClub && !preview && (
          <div style={{ display: "flex", marginTop: "20px" }}>
            <CTA autoWidth secondary margins={false} to={`/challenges/${goal.get("id")}/details`}>
              {fm(messages.editDetails)}
            </CTA>
          </div>
        )}
      </div>
    </Card>
  );
};

const _Settings = ({ goal, metaForGoal, findRoute, preview, fm }) => {
  const route = goal.goalParams?.route_id && findRoute(goal.goalParams?.route_id); // eslint-disable-line
  const count = metaForGoal.community.participants || 0;
  const { isCurrentClub } = useContext(OrgContext);
  return (
    <>
      <div className={styles.infoRow}>
        <Icon i="md-calendar" size={20} />
        {formatDate(goal.startsOn)} &mdash; {formatDate(goal.endsOn)}
      </div>
      <div className={styles.infoRow}>
        <Icon i="md-people" size={20} />
        {isCurrentClub ? (
          fm(
            goal.goalParams.participation_type === "individual"
              ? messages.individualParticipation
              : messages.communityParticipation
          )
        ) : (
          <>
            {count}{" "}
            {fm(
              goal.goalParams.participation_type === "individual"
                ? messages.individualParticipants
                : messages.communityParticipants,
              { count }
            )}
          </>
        )}
      </div>
      {goal.goalParams?.max && (
        <>
          {goal.goalType === "distance" && (
            <TargetRow i="distance" msg={fm(messages.targetDistance)}>
              <Distance bigUnits value={goal.goalParams.max} />
            </TargetRow>
          )}
          {goal.goalType === "elevation_gain" && (
            <TargetRow i="elevation-icon" msg={fm(messages.targetElevation)}>
              <Elevation smallUnits value={goal.goalParams.max} />
            </TargetRow>
          )}
          {goal.goalType === "moving_time" && (
            <TargetRow i="md-stopwatch" msg={fm(messages.targetTime)}>
              {`${goal.goalParams.max / 3600} ${fm(commonNouns.hours, {
                count: goal.goalParams.max / 3600,
              })}`}
            </TargetRow>
          )}
        </>
      )}
      {!goal.goalParams?.has_target && ( // eslint-disable-line
        <>
          {goal.goalType === "distance" && <TargetRow i="distance">{fm(messages.mostDistance)}</TargetRow>}
          {goal.goalType === "elevation_gain" && <TargetRow i="elevation-icon">{fm(messages.mostElevation)}</TargetRow>}
          {goal.goalType === "moving_time" && <TargetRow i="md-stopwatch">{fm(messages.mostTime)}</TargetRow>}
        </>
      )}
      {goal.goalType === "distance" &&
        goal.goalParams.route_id && ( // eslint-disable-line
          <>
            {route ? (
              <TargetRow i="distance" msg={<Distance short bigUnits value={route.distance} />}>
                {route.name}
              </TargetRow>
            ) : (
              <CircleSpinner size={24} centered />
            )}
          </>
        )}
      {isCurrentClub && !preview && (
        <Link className={styles.settingsLink} to={`/challenges/${goal.get("id")}/settings`}>
          {fm(overviewMessages.viewSettings)}
        </Link>
      )}
    </>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    findRoute: (routeId) => routeSelectors.find(state, routeId),
  };
};

export const Settings = connect(mapStateToProps, null)(_Settings);

const TargetRow = ({ i, msg, children }) => {
  return (
    <div className={styles.targetRow}>
      <Icon i={i} size={20} />
      <div className={styles.text}>
        {children}
        {msg && <span>{msg}</span>}
      </div>
    </div>
  );
};
